import { FC, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import gsap from 'gsap'
import Swiper from 'swiper'

import css from './PageGallery.module.scss'

import { GalleryCopyType } from '@/data/fixed-pages/copy-gallery'
import { PageHandle, PageProps } from '@/data/types'

import { cleanupImageDataForGallery, updateCustomProperties } from '@/utils/basic-functions'
import { MOTION } from '@/utils/motion-values'
import sass from '@/utils/sass'

import useLayout from '@/hooks/use-layout'
import { useRefs } from '@/hooks/use-refs'

import BaseImage from '@/components/BaseImage/BaseImage'
import CarouselControls from '@/components/CarouselControls/CarouselControls'
import CarouselCounter from '@/components/CarouselCounter/CarouselCounter'
import CustomCursorGallery from '@/components/CustomCursorGallery/CustomCursorGallery'
import Label from '@/components/Label/Label'
import Paragraph from '@/components/Paragraph/Paragraph'

export interface PageGalleryProps extends PageProps {
  className?: string
  content: GalleryCopyType
}

export type ViewRefs = {
  root: HTMLDivElement
  leftSwiperContainerElRef: HTMLDivElement
  leftSwiperWrapperRef: HTMLDivElement
  rightSwiperContainerElRef: HTMLDivElement
  rightSwiperWrapperRef: HTMLDivElement
  labelRef: HTMLDivElement
  counterRef: HTMLDivElement
  controlsRef: HTMLDivElement
  labelWrapper: HTMLDivElement
  labelPara: HTMLParagraphElement
}

const DEBUG = false

const View: FC<PageGalleryProps> = ({ className, content, onReady }) => {
  const layout = useLayout()
  const handleRef = useRef<PageHandle>(null)
  const viewRefs = useRefs<ViewRefs>()
  const imagesSet = content.images
  const [mobileData, desktopData] = useMemo(() => cleanupImageDataForGallery(imagesSet), [imagesSet])

  const leftSwiperObject = useRef<Swiper | null>(null)
  const rightSwiperObject = useRef<Swiper | null>(null)
  const [leftSwiperInit, setLeftSwiperInit] = useState(false)
  const [rightSwiperInit, setRightSwiperInit] = useState(false)
  const [progress, setProgress] = useState(0)

  const [leftSlideTransitioning, setLeftSlideTransitioning] = useState(false)
  const [rightSlideTransitioning, setRightSlideTransitioning] = useState(false)

  const [labelValue, setLabelValue] = useState(
    layout.desktop ? desktopData.descriptions[0] : mobileData.descriptions[0]
  )

  // @NOTE: Preload prev and next only
  const nextIndex = (progress + 1) % desktopData.left.length
  const prevIndex = (progress - 1 + desktopData.left.length) % desktopData.left.length

  const animateDone = useRef(false)

  useEffect(() => {
    let readyTimeout: NodeJS.Timeout

    gsap.set(viewRefs.root.current, { opacity: 0 })

    if (leftSwiperInit && rightSwiperInit) {
      readyTimeout = setTimeout(() => {
        onReady?.(handleRef)
      }, MOTION.general.delayForLayout)
    }

    return () => {
      if (readyTimeout) clearTimeout(readyTimeout)
    }
  }, [onReady, leftSwiperInit, rightSwiperInit, viewRefs])

  useImperativeHandle(handleRef, () => ({
    animateIn: () => {
      const timeline = gsap.timeline().set(viewRefs.root.current, { opacity: 1 }, 0)

      if (layout.desktop) {
        // @NOTE: Animate the Gallery first items in here instead of the carousel

        const wrapper = viewRefs.leftSwiperWrapperRef.current
        const firstSlide = wrapper?.querySelector('.swiper-slide-active')
        const firstImage = firstSlide?.querySelector('img')

        if (!firstSlide || !firstImage) {
          return timeline
        }

        gsap.set(firstSlide, { yPercent: 100 })
        gsap.set(firstImage, { scale: 1.2 })

        timeline
          .to(
            firstSlide,
            {
              yPercent: 0,
              duration: 1.2,
              ease: 'power2.out', // to match ease-out of the swiper
              onComplete: () => {
                animateDone.current = true
              }
            },
            '>+0.334'
          )
          .to(
            firstImage,
            {
              scale: 1,
              duration: 1.2,
              ease: 'ease-scale'
            },
            '<'
          )
      }

      timeline
        .fromTo(
          viewRefs.rightSwiperContainerElRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 0.8, ease: 'ease-opacity' },
          '<+0.334'
        ) //
        .fromTo(
          [viewRefs.labelWrapper.current, viewRefs.counterRef.current, viewRefs.controlsRef.current],
          {
            opacity: 0
          },
          { opacity: 1, stagger: 0.2, duration: 0.8, ease: 'ease-opacity' },
          '<+0.1'
        )

      const rightSwiper = rightSwiperObject.current

      if (rightSwiper) {
        const prevSlide = rightSwiper.slides[rightSwiper.previousIndex]
        const currentSlide = rightSwiper.slides[rightSwiper.activeIndex]

        timeline
          .to(prevSlide, { opacity: 0, duration: 0.8, ease: 'ease-opacity', overwrite: true }, '<')
          .to(currentSlide, { opacity: 1, duration: 0.8, ease: 'ease-opacity', overwrite: true }, '<+0.1')
      }

      return timeline
    },
    animateOut: () => {
      return gsap.timeline().to(viewRefs.root.current, { autoAlpha: 0, duration: 0.8, ease: 'ease-opacity' })
    }
  }))

  useEffect(() => {
    if (layout.desktop) {
      if (desktopData.left[progress]?.style?.backButtonColor === 'white') {
        updateCustomProperties({
          backButtonColor: 'white'
        })
      } else {
        updateCustomProperties({
          backButtonColor: 'black'
        })
      }
    }
  }, [progress, layout, desktopData])

  useEffect(() => {
    updateCustomProperties({
      backButtonVisibility: 'visible',
      backButtonColor: sass.black,
      backButtonOpacity: '1',
      navButtonBackground: sass.black,
      navButtonColor: sass.white,
      navButtonBorder: 'transparent'
    })
  }, [])

  const handleLeftSlideChange = useCallback(
    (swiper: Swiper) => {
      const currentSlide = swiper.slides[swiper.activeIndex]
      const image = currentSlide.querySelector('img')

      if (!layout.desktop || !animateDone.current || !image) {
        return
      }

      gsap.fromTo(
        image,
        {
          scale: 1.2
        },
        {
          scale: 1,
          duration: 1.2,
          ease: 'ease-scale'
        }
      )
    },

    [layout.desktop]
  )

  const handleRightSlideChange = useCallback(
    (swiper: Swiper) => {
      setProgress(swiper?.realIndex)

      if (!layout.desktop || !animateDone.current) {
        return
      }

      const prevSlide = swiper.slides[swiper.previousIndex]
      const currentSlide = swiper.slides[swiper.activeIndex]

      gsap
        .timeline()
        .to(prevSlide, { opacity: 0, duration: 0.8, ease: 'ease-opacity', overwrite: true }, 0)
        .to(currentSlide, { opacity: 1, duration: 0.8, ease: 'ease-opacity', overwrite: true }, '>=+0.1')
    },
    [layout.desktop]
  )

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const page = urlParams.get('page')
    let initialSlide = 0

    if (page) {
      initialSlide = parseInt(page, 10) - 1
    }

    const swiperTransitionDuration = MOTION.general.carousel.carouselTransitionInMs // ms

    // [LEFT] Big images to the left on desktop
    if (viewRefs.leftSwiperContainerElRef.current) {
      leftSwiperObject.current = new Swiper(viewRefs.leftSwiperContainerElRef.current, {
        slidesPerView: 1,
        initialSlide,
        spaceBetween: 0,
        speed: swiperTransitionDuration, // ms
        wrapperClass: css.leftGalleryWrapper,
        slideClass: css.leftGalleryItem,
        loop: true,
        on: {
          afterInit: () => {
            setLeftSwiperInit(true)
          },
          slideChangeTransitionStart: (swiper) => {
            handleLeftSlideChange(swiper)
            setLeftSlideTransitioning(true)
          },
          slideChangeTransitionEnd: () => {
            setLeftSlideTransitioning(false)
          }
        },
        allowTouchMove: false,
        direction: 'vertical'
      })
    }

    // [RIGHT] mobile and tiny images
    if (viewRefs.rightSwiperContainerElRef.current) {
      rightSwiperObject.current = new Swiper(viewRefs.rightSwiperContainerElRef.current, {
        slidesPerView: 1,
        spaceBetween: 0,
        initialSlide,
        wrapperClass: css.rightGalleryWrapper,
        slideClass: css.rightGalleryItem,
        loop: true,
        allowTouchMove: layout.desktop ? false : true,
        on: {
          afterInit: () => {
            setRightSwiperInit(true)
          },
          slideChangeTransitionStart: (swiper) => {
            handleRightSlideChange(swiper)
            setRightSlideTransitioning(true)
          },
          slideChangeTransitionEnd: () => {
            setRightSlideTransitioning(false)
          }
        },
        speed: layout.desktop ? 0 : 667 // ms
      })
    }

    return () => {
      leftSwiperObject.current?.destroy()
      leftSwiperObject.current = null
      rightSwiperObject.current?.destroy()
      rightSwiperObject.current = null
    }
  }, [handleLeftSlideChange, handleRightSlideChange, layout.desktop, viewRefs])

  const moveCarouselsForward = useCallback(() => {
    if (leftSlideTransitioning || rightSlideTransitioning) return

    leftSwiperObject.current?.slideNext()
    rightSwiperObject.current?.slideNext()
  }, [leftSlideTransitioning, rightSlideTransitioning])

  const moveCarouselsBackward = useCallback(() => {
    if (leftSlideTransitioning || rightSlideTransitioning) return

    leftSwiperObject.current?.slidePrev()
    rightSwiperObject.current?.slidePrev()
  }, [leftSlideTransitioning, rightSlideTransitioning])

  useEffect(() => {
    // Detect arrow left and right key presses
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        moveCarouselsBackward()
      } else if (e.key === 'ArrowRight') {
        moveCarouselsForward()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [moveCarouselsBackward, moveCarouselsForward])

  // @NOTE: Label Motion
  useEffect(() => {
    const value = layout.desktop ? desktopData.descriptions[progress] : mobileData.descriptions[progress]

    if (!value || value === labelValue) return

    gsap
      .timeline()
      .to(
        viewRefs.labelRef.current,
        {
          opacity: 0,
          duration: 0.8,
          ease: 'ease-opacity',
          overwrite: true
        },
        0
      )
      .add(() => {
        setLabelValue(value)
      }, '>')
      .to(
        viewRefs.labelRef.current,
        {
          opacity: 1,
          duration: 0.8,
          ease: 'ease-opacity'
        },
        '>=+0.1'
      )
  }, [progress, layout, desktopData, mobileData, labelValue, viewRefs])

  return (
    <div className={classNames('PageGallery', css.root, className)} ref={viewRefs.root}>
      {layout.desktop ? (
        <div className={css.leftGallery} ref={viewRefs.leftSwiperContainerElRef}>
          <div className={css.leftGalleryWrapper} ref={viewRefs.leftSwiperWrapperRef}>
            {desktopData.left.map(({ imageData }, i) => (
              <div className={classNames(css.leftGalleryItem, 'CursorLeftGallery')} key={i}>
                {imageData ? (
                  <BaseImage
                    className={css.galleryImage}
                    data={imageData}
                    fill={false}
                    loading={i === nextIndex || i === prevIndex ? 'eager' : 'lazy'}
                  />
                ) : null}
                {DEBUG ? <div className={css.leftDebug}>{`left-${i + 1}`}</div> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {layout.device ? null : (
        <CustomCursorGallery handleClickLeft={moveCarouselsBackward} handleClickRight={moveCarouselsForward} />
      )}

      <div className={classNames(css.rightGalleryMegaWrapper, 'CursorRightGallery')}>
        <div className={css.description} ref={viewRefs.labelWrapper}>
          <Paragraph paragraphType={2} italic ref={viewRefs.labelPara}>
            Serie
          </Paragraph>
          <Label labelType={3} ref={viewRefs.labelRef}>
            {labelValue}
          </Label>
        </div>

        <div className={css.rightGallery} ref={viewRefs.rightSwiperContainerElRef}>
          <div className={css.rightGalleryWrapper} ref={viewRefs.rightSwiperWrapperRef}>
            {(layout.desktop ? desktopData.right : mobileData.images).map(({ imageData, style }, i) => (
              <div
                className={classNames(css.rightGalleryItem, {
                  [css.rightGalleryItemException]: desktopData.isRightException[i]
                })}
                key={`${layout.desktop ? 'desktop-' : 'mobile-'}${i}`}
              >
                {imageData ? (
                  <BaseImage
                    className={classNames(css.galleryImage, {
                      [css.mobileContain]: layout.device && style?.cover === 'contain'
                    })}
                    data={imageData}
                    fill={false}
                    loading={i === nextIndex || i === prevIndex ? 'eager' : 'lazy'}
                  />
                ) : null}
                {DEBUG ? <div className={css.rightDebug}>{`right-${i + 1}`}</div> : null}
              </div>
            ))}
          </div>
        </div>

        <CarouselCounter
          ref={viewRefs.counterRef}
          className={css.counter}
          current={progress + 1}
          total={layout.desktop ? desktopData.left.length : mobileData.images.length}
        />

        <CarouselControls
          ref={viewRefs.controlsRef}
          className={css.controls}
          backCallback={moveCarouselsBackward}
          forwardCallback={moveCarouselsForward}
        />
      </div>
    </div>
  )
}

View.displayName = 'PageGallery-View'

// Controller (handles global state, router, data fetching, etc. Feeds props to the view component)
const PageGallery: FC<PageGalleryProps> = (props) => {
  return <View {...props} />
}

PageGallery.displayName = 'PageGallery'

export default memo(PageGallery)
